import { useCustomerRegistryRefresh } from '../utils/customerRegistryEvents';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaComments,
  FaFacebook,
  FaInstagram,
  FaPaperPlane,
  FaSyncAlt,
  FaWhatsapp,
} from 'react-icons/fa';
import api from '../api';

const PROVIDER = {
  whatsapp: { label: 'WhatsApp', icon: FaWhatsapp },
  instagram: { label: 'Instagram', icon: FaInstagram },
  facebook: { label: 'Facebook', icon: FaFacebook },
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function CRMChannelInbox({ customerId, provider, compact = false }) {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const selectedRef = useRef(null); selectedRef.current = selectedId;
  const messageRequest = useRef(0);
  const drafts = useRef({});
  const replyLock = useRef(false);
  const [replyBusy, setReplyBusy] = useState(false);

  const loadConversations = useCallback(async (silent = false) => {
    if (!silent) setBusy('list');
    try {
      const response = await api.get('/crm/channels/conversations', {
        skipTransientRetry: silent,
        ...(customerId || provider ? { params: { ...(customerId ? { customerId } : {}), ...(provider ? { provider } : {}) } } : {}),
      });
      const items = Array.isArray(response.data?.conversations)
        ? response.data.conversations
        : [];
      setConversations(items);
      setError('');
      setSelectedId((current) => {
        if (current && items.some((item) => item.id === current)) return current;
        return items[0]?.id || null;
      });
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.error || 'Não foi possível carregar as mensagens.');
      }
    } finally {
      if (!silent) setBusy('');
    }
  }, [customerId, provider]);

  const loadMessages = useCallback(async (conversationId, silent = false) => {
    if (!conversationId) {
      setDetail(null);
      return;
    }
    if (selectedRef.current !== conversationId) return;
    const request = ++messageRequest.current;
    if (!silent) { setBusy('messages'); setDetail(null); }
    try {
      const response = await api.get(
        '/crm/channels/conversations/' + conversationId + '/messages',
        { skipTransientRetry: silent },
      );
      if (request !== messageRequest.current || selectedRef.current !== conversationId) return;
      setDetail(response.data || null);
      setError('');
    } catch (err) {
      if (!silent && selectedRef.current === conversationId) {
        setError(err.response?.data?.error || 'Não foi possível abrir a conversa.');
      }
    } finally {
      if (!silent && selectedRef.current === conversationId) setBusy('');
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return undefined;
    }
    setReply(drafts.current[selectedId] || '');
    loadMessages(selectedId);
    const timer = window.setInterval(() => {
      loadConversations(true);
      loadMessages(selectedId, true);
    }, 10000);
    return () => window.clearInterval(timer);
  }, [loadConversations, loadMessages, selectedId]);

  useCustomerRegistryRefresh(kind=>{
    if(kind!=='templates') { loadConversations(true); if(selectedId)loadMessages(selectedId,true); }
  });

  const selectedConversation = useMemo(
    () => conversations.find((item) => item.id === selectedId) || null,
    [conversations, selectedId],
  );

  const sendReply = async (event) => {
    event.preventDefault();
    const text = reply.trim();
    if (!selectedId || !text || replyLock.current || detail?.conversation?.id !== selectedId) return;
    const targetId = selectedId;
    replyLock.current = true; setReplyBusy(true);
    setError('');
    try {
      await api.post('/crm/channels/conversations/' + targetId + '/reply', { text });
      drafts.current[targetId] = '';
      if (selectedRef.current === targetId) setReply('');
      await Promise.all([
        loadMessages(targetId, true),
        loadConversations(true),
      ]);
    } catch (err) {
      setError(err.response?.data?.error || 'Não foi possível enviar a resposta.');
    } finally {
      replyLock.current = false; setReplyBusy(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      {!compact && <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
            <FaComments />
          </span>
          <div>
            <h2 className="font-extrabold text-slate-900">Mensagens dos canais</h2>
            <p className="text-xs text-slate-500">
              Receba e responda WhatsApp, Instagram e Facebook sem sair do CRM.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => loadConversations()}
          disabled={busy === 'list'}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
        >
          <FaSyncAlt className={busy === 'list' ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </header>}

      {error && (
        <div className="mx-5 mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
          {error}
        </div>
      )}

      <div className={compact ? "grid min-h-[340px] lg:grid-cols-[260px_minmax(0,1fr)]" : "grid min-h-[560px] lg:grid-cols-[340px_minmax(0,1fr)]"}>
        <aside className="border-b border-slate-200 bg-slate-50/70 lg:border-b-0 lg:border-r">
          <div className="max-h-[650px] overflow-y-auto p-3">
            {conversations.map((item) => {
              const config = PROVIDER[item.provider] || {};
              const Icon = config.icon || FaComments;
              const active = selectedId === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => { if (item.id !== selectedId) { setDetail(null); setSelectedId(item.id); } }}
                  className={
                    'mb-2 w-full rounded-2xl border p-3 text-left transition ' +
                    (active
                      ? 'border-blue-200 bg-white shadow-sm'
                      : 'border-transparent hover:border-slate-200 hover:bg-white')
                  }
                >
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-blue-700 shadow-sm">
                      <Icon />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <strong className="truncate text-sm text-slate-900">
                          {item.contactName || item.externalContactId}
                        </strong>
                        {item.unreadCount > 0 && (
                          <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white">
                            {item.unreadCount}
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block text-[11px] font-semibold text-slate-400">
                        {config.label || item.provider} · {item.accountName}
                      </span>
                      <span className="mt-1 block truncate text-xs text-slate-500">
                        {item.lastMessagePreview || 'Sem prévia'}
                      </span>
                      <span className="mt-1 block text-[10px] text-slate-400">
                        {formatDateTime(item.lastMessageAt)}
                      </span>
                    </span>
                  </div>
                </button>
              );
            })}
            {!conversations.length && busy !== 'list' && (
              <div className="m-3 rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
                {customerId ? 'Nenhuma conversa recebida deste cliente no canal conectado.' : 'Nenhuma conversa recebida ainda. Depois que o webhook do canal estiver ativo, as novas mensagens aparecem aqui automaticamente.'}
              </div>
            )}
          </div>
        </aside>

        <div className={compact ? "flex min-h-[340px] flex-col" : "flex min-h-[560px] flex-col"}>
          {selectedConversation ? (
            <>
              <div className="border-b border-slate-100 px-5 py-4">
                <div className="font-bold text-slate-900">
                  {detail?.conversation?.contactName ||
                    selectedConversation.contactName ||
                    selectedConversation.externalContactId}
                </div>
                <div className="text-xs text-slate-500">
                  {(PROVIDER[selectedConversation.provider]?.label || selectedConversation.provider) +
                    ' · ' +
                    selectedConversation.accountName}
                </div>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50/40 p-5">
                {(detail?.messages || []).map((message) => {
                  const outbound = message.direction === 'OUTBOUND';
                  return (
                    <div
                      key={message.id}
                      className={'flex ' + (outbound ? 'justify-end' : 'justify-start')}
                    >
                      <div
                        className={
                          'max-w-[82%] rounded-2xl px-4 py-3 text-sm shadow-sm ' +
                          (outbound
                            ? 'bg-[#071f54] text-white'
                            : 'border border-slate-200 bg-white text-slate-800')
                        }
                      >
                        <div className="whitespace-pre-wrap break-words">{message.text}</div>
                        <div
                          className={
                            'mt-1.5 flex items-center gap-2 text-[10px] ' +
                            (outbound ? 'text-blue-100' : 'text-slate-400')
                          }
                        >
                          <span>{formatDateTime(message.sentAt)}</span>
                          {outbound && <span>{message.status}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {busy === 'messages' && (
                  <p className="text-center text-xs text-slate-400">Carregando conversa...</p>
                )}
              </div>

              <form
                onSubmit={sendReply}
                className="flex items-end gap-3 border-t border-slate-200 bg-white p-4"
              >
                <textarea
                  value={reply}
                  onChange={(event) => { setReply(event.target.value); drafts.current[selectedId] = event.target.value; }}
                  disabled={detail?.conversation?.id !== selectedId || replyBusy}
                  rows={2}
                  maxLength={4000}
                  placeholder="Digite sua resposta..."
                  className="min-h-[48px] flex-1 resize-none rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                />
                <button
                  type="submit"
                  disabled={!reply.trim() || replyBusy || detail?.conversation?.id !== selectedId}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-[#071f54] px-5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FaPaperPlane />
                  {replyBusy ? 'Enviando' : 'Responder'}
                </button>
              </form>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-8 text-center">
              <div>
                <FaComments className="mx-auto mb-3 text-4xl text-slate-300" />
                <p className="font-semibold text-slate-600">Selecione uma conversa</p>
                <p className="mt-1 text-sm text-slate-400">
                  As mensagens recebidas e respondidas ficam registradas no CRM.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
